// src/services/syncTravelHistory.js
//
// The core "pull historical positions from the GPS server and reconstruct
// idle/moving transitions" logic — used both by the manual Sync button in
// Settings and by the automatic per-truck sync on the Travel History page.

import { fetchHistoricalPositions, reverseGeocode } from './traccar';

const MOVING_THRESHOLD_KMH = 3;
const knotsToKmh = (knots) => (knots || 0) * 1.852;
export const DEFAULT_SYNC_LOOKBACK_DAYS = 30;
export const GEOCODE_CAP_PER_TRUCK = 40; // keeps a big backlog from taking forever — see fallback below
const sleep = (ms) => new Promise(r => setTimeout(r, ms));

// History is fetched from the GPS server in windows instead of one request
// for the whole range. A truck with no history yet used to ask for 30 days
// in a single request — for a device reporting every few seconds that's a
// huge response, which the server (or a proxy in front of it) can drop
// partway, surfacing in the browser as a bare "NetworkError". Small windows
// keep each response manageable, and a failure only costs that window.
const WINDOW_MS = 24 * 60 * 60 * 1000;      // start with 1 day per request
const MIN_WINDOW_MS = 60 * 60 * 1000;       // never split below 1 hour
const MAX_WINDOW_FAILURES = 8;              // per truck, so a struggling server isn't hammered
const fmtTime = (ms) => new Date(ms).toLocaleString('en-PH', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

function distanceMeters(lat1, lng1, lat2, lng2) {
  const R = 6371000;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// Builds idle<->moving transitions from a chronological position stream,
// fed in one window at a time. State (previous status, last kept entry) is
// carried across windows so a transition that straddles a window boundary
// is still detected correctly and jitter debouncing keeps working.
//
// Skips positions with no usable coordinates (some devices report a fix with
// null lat/lng during a signal dropout), and debounces GPS jitter: a
// "transition" that's both very close in distance and time to the previous
// kept entry is almost certainly noise.
//
// `seed` (optional) is the truck's most recent ALREADY-SAVED entry. When
// resuming a sync, the fetch starts at that entry's timestamp (inclusive), so
// the very same position comes back and — without knowing about the saved
// entry — was re-imported as a duplicate every single re-sync. Seeding starts
// the builder in the saved entry's state, and ignores anything at or before
// its timestamp, so only genuine changes since then become new entries.
export function createTransitionBuilder(truckId, seed = null) {
  const transitions = [];
  let prevStatus = seed ? seed.type : null;
  let lastKept = seed ? { lat: seed.lat, lng: seed.lng, timestamp: seed.timestamp } : null;
  const seedMs = seed ? new Date(seed.timestamp).getTime() : null;
  let skippedNoCoords = 0;

  return {
    feed(positions) {
      const valid = [];
      for (const pos of positions) {
        if (pos.latitude != null && pos.longitude != null && Number.isFinite(pos.latitude) && Number.isFinite(pos.longitude)) {
          valid.push(pos);
        } else {
          skippedNoCoords++;
        }
      }
      valid.sort((a, b) => new Date(a.fixTime) - new Date(b.fixTime));

      valid.forEach(pos => {
        if (seedMs != null && new Date(pos.fixTime).getTime() <= seedMs) return; // already covered by the saved entry
        const kmh = knotsToKmh(pos.speed);
        const status = kmh > MOVING_THRESHOLD_KMH ? 'moving' : 'idle';
        if (status === prevStatus) return;
        if (lastKept) {
          const distance = distanceMeters(lastKept.lat, lastKept.lng, pos.latitude, pos.longitude);
          const secondsSince = (new Date(pos.fixTime) - new Date(lastKept.timestamp)) / 1000;
          if (distance < 40 && secondsSince < 90) return; // jitter — skip without updating prevStatus
        }
        const entry = {
          truckId,
          type: status,
          lat: pos.latitude,
          lng: pos.longitude,
          address: pos.address || null,
          speed: Math.round(kmh),
          timestamp: pos.fixTime,
          duration: null,
        };
        transitions.push(entry);
        lastKept = entry;
        prevStatus = status;
      });
    },
    finish() {
      for (let i = 0; i < transitions.length - 1; i++) {
        transitions[i].duration = Math.round((new Date(transitions[i + 1].timestamp) - new Date(transitions[i].timestamp)) / 60000);
      }
      return transitions;
    },
    get skippedNoCoords() { return skippedNoCoords; },
  };
}

// One-shot version, kept for callers that already have all positions in hand.
export function reconstructTransitions(truckId, positions) {
  const builder = createTransitionBuilder(truckId);
  builder.feed(positions);
  return builder.finish();
}

/**
 * Syncs ONE truck's travel history from the GPS server — incremental
 * (resumes from its most recent existing entry, or a fixed lookback window
 * if it has none yet).
 *
 * `onProgress(phase, extra)`:
 *   'positions' { current, total }  — window progress
 *   'geocoding' { current, total }  — location-name lookups
 *   'warning'   { message }         — partial problems (skipped time ranges,
 *                                     stopped early) that didn't stop the sync
 *
 * Returns the new transitions (geocoded), or [] if nothing new. Does NOT
 * persist them — that's left to the caller (bulkAddTravelHistory). Throws
 * only if NOTHING could be fetched; if some windows worked, it returns what
 * it got and reports the rest through 'warning'.
 */
export async function syncOneTruck(truck, traccarDeviceId, existingHistoryForTruck, onProgress) {
  const latest = existingHistoryForTruck.length
    ? existingHistoryForTruck.reduce((a, b) => new Date(a.timestamp) > new Date(b.timestamp) ? a : b)
    : null;
  const fromMs = latest
    ? new Date(latest.timestamp).getTime()
    : Date.now() - DEFAULT_SYNC_LOOKBACK_DAYS * 24 * 60 * 60 * 1000;
  const toMs = Date.now();

  if (toMs - fromMs < 60000) return []; // less than a minute of new range — nothing worth syncing yet

  const builder = createTransitionBuilder(
    truck.id,
    latest ? { type: latest.type, lat: latest.lat, lng: latest.lng, timestamp: latest.timestamp } : null
  );
  const totalWindows = Math.max(1, Math.ceil((toMs - fromMs) / WINDOW_MS));

  let cursor = fromMs;
  let windowMs = WINDOW_MS;
  let failures = 0;
  let fetchedAny = false;
  let lastError = null;
  let stoppedEarlyAt = null;
  const skippedRanges = [];

  onProgress?.('positions', { current: 0, total: totalWindows });

  while (cursor < toMs) {
    const winEnd = Math.min(cursor + windowMs, toMs);
    try {
      const positions = await fetchHistoricalPositions(traccarDeviceId, new Date(cursor).toISOString(), new Date(winEnd).toISOString());
      builder.feed(positions);
      fetchedAny = true;
      cursor = winEnd;
      windowMs = Math.min(WINDOW_MS, windowMs * 2); // recover toward full-size windows after a success
      onProgress?.('positions', { current: Math.min(totalWindows, Math.ceil((cursor - fromMs) / WINDOW_MS)), total: totalWindows });
      await sleep(120); // be gentle on the GPS server between requests
    } catch (err) {
      lastError = err;
      // Client errors (e.g. 401 auth) won't be fixed by retrying smaller windows
      const retryable = err.status == null || err.status >= 500 || err.status === 429 || err.status === 408;
      if (!retryable) throw err;

      failures++;
      if (failures > MAX_WINDOW_FAILURES) { stoppedEarlyAt = cursor; break; }

      if (winEnd - cursor > MIN_WINDOW_MS) {
        windowMs = Math.max(MIN_WINDOW_MS, Math.floor((winEnd - cursor) / 2)); // retry the same start with a smaller window
      } else {
        skippedRanges.push(`${fmtTime(cursor)} – ${fmtTime(winEnd)}`); // even 1 hour won't come back; skip just that hour
        cursor = winEnd;
      }
    }
  }

  if (!fetchedAny) throw lastError || new Error('The GPS server returned no history');

  if (skippedRanges.length > 0) {
    onProgress?.('warning', {
      message: `${skippedRanges.length} time range${skippedRanges.length === 1 ? '' : 's'} couldn't be fetched from the GPS server and ${skippedRanges.length === 1 ? 'was' : 'were'} skipped (${skippedRanges.slice(0, 2).join('; ')}${skippedRanges.length > 2 ? '; …' : ''})`,
    });
  }
  if (stoppedEarlyAt != null) {
    onProgress?.('warning', {
      message: `the GPS server kept failing after ${fmtTime(stoppedEarlyAt)} — synced up to there; sync again later to continue`,
    });
  }
  if (builder.skippedNoCoords > 0) {
    console.warn(`[SyncTravelHistory] ${truck.plateNumber}: skipped ${builder.skippedNoCoords} positions with missing/invalid coordinates`);
  }

  const transitions = builder.finish();
  if (transitions.length === 0) return [];

  // Resolve a real location name for every entry that doesn't already have
  // one. Rate-limited to stay within the free geocoding service's usage
  // policy — for a large backlog only the most recent GEOCODE_CAP_PER_TRUCK
  // get a real address; the rest fall back to formatted coordinates.
  const needsAddress = transitions.filter(t => !t.address);
  const toGeocode = needsAddress.slice(-GEOCODE_CAP_PER_TRUCK);
  const tooManyToGeocode = needsAddress.length - toGeocode.length;

  for (let n = 0; n < toGeocode.length; n++) {
    onProgress?.('geocoding', { current: n + 1, total: toGeocode.length });
    toGeocode[n].address = await reverseGeocode(toGeocode[n].lat, toGeocode[n].lng);
    await sleep(1100);
  }
  needsAddress.slice(0, tooManyToGeocode).forEach(t => {
    t.address = `${t.lat.toFixed(5)}, ${t.lng.toFixed(5)}`;
  });

  return transitions;
}
